<template>
  <b-form ref="form" @submit.stop.prevent="">
    <b-form-group label-for="reisekuerzel" :invalid-feedback="validateState('reisekuerzel', true)">
      <slot name="label">
        <div class="d-flex align-items-center mb-2">
          <label v-t="'REISEN.COMMON.REISEKUERZEL'" class="mb-0"></label>
          <b-button
            class="btn-xs btn py-1 px-2 ml-2 justify-content-center align-items-center d-flex"
            v-b-tooltip.hover
            :title="$t('REISEN.POPOVERTEXT.Widget1')"
          >
            <Info :size="14" class="m-0 p-0" />
          </b-button>
        </div>
      </slot>
      <b-form-input
        id="reisekuerzel"
        disabled
        v-model="$v.form.reisekuerzel.$model"
        :formatter="v => v.toUpperCase()"
        :state="validateState('reisekuerzel')"
        type="text"
        trim
      ></b-form-input>
    </b-form-group>
    <b-form-group label="Reisetitel" label-for="titel">
      <b-form-input
        id="titel"
        type="text"
        v-model="$v.form.titel.$model"
        :disabled="!isUserAllowedToEdit"
        :state="$v.form.titel.$model?.length > 0"
      ></b-form-input>
    </b-form-group>

    <b-form-group
      :invalid-feedback="validateState('country', true)"
      :state="validateState('country')"
      :label="$t('REISEN.LANDEINGEBEN')"
      label-for="country"
    >
      <AppSelect
        id="country"
        v-model="$v.form.country.$model"
        :options="countrySelectOptions"
        label="Land"
        placeholder="Land wählen"
        :disabled="!isUserAllowedToEdit"
        :state="validateState('country')"
      />
    </b-form-group>
    <b-form-group label="Weitere Länder" label-for="weitereLaender">
      <AppSelect
        id="weitereLaender"
        v-model="form.weitereLaender"
        :options="countriesOptions"
        clearable
        :disabled="!isUserAllowedToEdit"
        label="Weitere Länder"
        placeholder="Weitere Länder auswählen"
        multiple
      />
    </b-form-group>
    <b-form-group
      :invalid-feedback="validateState('region', true)"
      :state="validateState('region')"
      :label="$t('REISEN.COMMON.REGION')"
      label-for="region"
    >
      <AppSelect
        id="region"
        v-model="$v.form.region.$model"
        :options="regionOptions"
        label="Region"
        placeholder="Region wählen"
        :disabled="!isUserAllowedToEdit"
        :state="validateState('region')"
      />
    </b-form-group>

    <b-form-group
      :invalid-feedback="validateState('prioritaet', true)"
      :state="validateState('prioritaet')"
      :label="$t('REISEN.PRIORITAETEINGEBEN')"
      label-for="prioritaet"
    >
      <AppSelect
        id="prioritaet"
        v-model="$v.form.prioritaet.$model"
        sort="none"
        :options="prioritaetSelectOptions"
        label="Priorität"
        placeholder="Priorität wählen"
        hide-search
        :disabled="!isUserAllowedToEdit"
        :state="validateState('prioritaet')"
      />
    </b-form-group>
    <b-form-group
      label="Regionsmanagerin"
      label-for="regionsmanager"
      :invalid-feedback="validateState('regionsmanager', true)"
      :state="validateState('regionsmanager')"
    >
      <AppSelect
        id="regionsmanager"
        v-model="$v.form.regionsmanager.$model"
        :options="regionsmanagerOptions"
        label="Regionsmanagerin"
        placeholder="Regionsmanagerin wählen"
        hide-search
        clearable
        :disabled="!isUserAllowedToEdit"
        :state="validateState('regionsmanager')"
      />
    </b-form-group>

    <b-form-group label-for="einreisebestimmungenLink">
      <slot name="label">
        <div class="d-flex align-items-center mb-2">
          <label class="mb-0">Einreisebestimmungen (Link)</label>
          <b-button
            class="btn-xs btn py-1 px-2 ml-2 justify-content-center align-items-center d-flex"
            v-b-tooltip.hover.top
            :title="$t('REISEN.POPOVERTEXT.Einreisebestimmungen')"
          >
            <Info :size="14" class="m-0 p-0" />
          </b-button>
        </div>
      </slot>
      <b-form-input
        id="einreisebestimmungenLink"
        v-model="form.einreisebestimmungenLink"
        :disabled="!isUserAllowedToEdit"
        placeholder="https://www.skr.de/?type=1568711051&destinations=NA"
      ></b-form-input>
    </b-form-group>

    <b-form-group class="mb-3">
      <b-form-checkbox
        v-model="$v.form.isKritischeDestination.$model"
        :disabled="!isUserAllowedToEdit"
        style="display: flex; align-items: center"
      >
        Kritische Destination
      </b-form-checkbox>
    </b-form-group>

    <b-form-group class="mb-3">
      <b-form-checkbox
        id="automatischeAufgabeMtzErreicht"
        switch
        v-model="$v.form.automatischeAufgabeMtzErreicht.$model"
        :disabled="!isUserAllowedToEdit"
      >
        Automatische Aufgabe wird bei erreichter MTZ erstellt
      </b-form-checkbox>
    </b-form-group>
    <b-form-group class="mb-3">
      <b-form-checkbox
        id="automatischeAufgabeTktl"
        switch
        v-model="$v.form.automatischeAufgabeTktl.$model"
        :disabled="!isUserAllowedToEdit"
      >
        Automatische Aufgabe “Kurzes TKTL freischalten/deaktivieren" wird erstellt
      </b-form-checkbox>
    </b-form-group>
    <b-form-group class="mb-3">
      <b-form-checkbox
        id="automatischeAufgabeFahrzeugpruefung"
        switch
        v-model="$v.form.automatischeAufgabeFahrzeugpruefung.$model"
        :disabled="!isUserAllowedToEdit"
      >
        Automatische Aufgabe "Terminspezifische Fahrzeugprüfung" wird erstellt
      </b-form-checkbox>
    </b-form-group>
    <b-button
      v-allowedRoles="[Role.ADMIN, Role.PM, Role.DOM, Role.YIELD]"
      variant="primary"
      class="dialog-btn mr-3"
      @click="save"
    >
      Speichern
    </b-button>
  </b-form>
</template>

<script>
import { UPDATE_REISE } from '@/core/produkte/reisen/stores/reisen.module.js';
import { validationMixin } from 'vuelidate';
import { maxLength, minLength, required } from 'vuelidate/lib/validators';
import { mapGetters, mapState } from 'vuex';

import { alleLaender, landOptions } from '@/core/common/iso3166Countries.js';
import { parseFullUsername } from '@/core/common/helpers/utils.js';
import configRegions from '@/core/produkte/reisen/regions.config.json';
import configPirority from '@/core/produkte/reisen/priority.config.json';
import AppSelect from '@/components/common/app-select/app-select.vue';
import { Info } from 'lucide-vue';

export default {
  mixins: [validationMixin],
  components: { AppSelect, Info },
  name: 'ReisenStandardDataEditForm',
  props: {
    reise: Object,
  },
  data() {
    return {
      form: {
        reisekuerzel: null,
        country: null,
        weitereLaender: [],
        region: null,
        prioritaet: null,
        titel: '',
        regionsmanager: null,
        einreisebestimmungenLink: null,
        isKritischeDestination: null,
        automatischeAufgabeMtzErreicht: null,
        automatischeAufgabeTktl: null,
        automatischeAufgabeFahrzeugpruefung: false,
      },
      error: {},
    };
  },
  watch: {
    reise: {
      deep: true,
      immediate: true,
      handler: function (newVal, oldVal) {
        if (newVal) {
          this.setFormToReise(newVal);
        }
      },
    },
  },
  computed: {
    ...mapGetters(['currentUserId', 'parsedRoles']),
    ...mapState({ users: state => state.users['users'] }),
    isUserAllowedToEdit() {
      return (
        this.parsedRoles.includes('Admin') ||
        this.parsedRoles.includes('Pm') ||
        this.parsedRoles.includes('Dom') ||
        this.parsedRoles.includes('Yield')
      );
    },
    regionOptions() {
      return Object.entries(configRegions)
        .reduce((acc, cur) => {
          acc.push({ value: cur[1].value, text: this.$t('REISEN.REGIONS.' + cur[1].value) });
          return acc;
        }, [])
        .sort((a, b) => a.text.localeCompare(b.text));
    },
    regionsmanagerOptions() {
      const allowedNames = ['Sylvia Kehr', 'Stefanie Geyer'];
      const fullName = user => `${user.givenName} ${user.surname}`;

      return this.users
        .filter(user => user.department === 'Yield Fulfillment' && allowedNames.includes(fullName(user)))
        .map(user => ({
          value: user.id,
          text: parseFullUsername(user),
        }));
    },
    countriesOptions() {
      return landOptions();
    },
    countrySelectOptions() {
      const options = [...this.countriesOptions];
      if (this.form.country && !alleLaender()[this.form.country]) {
        options.unshift({ value: this.form.country, text: this.form.country, disabled: true });
      }
      return options;
    },
    prioritaetOptions() {
      return configPirority || [];
    },
    prioritaetSelectOptions() {
      return Object.values(this.prioritaetOptions).map(prio => ({ value: prio.value, text: prio.value }));
    },
  },
  validations: {
    form: {
      reisekuerzel: {
        required,
        minLength: minLength(6),
        maxLength: maxLength(6),
      },
      country: {
        required,
      },
      region: {
        required,
      },
      prioritaet: {
        required,
      },
      titel: { required },
      regionsmanager: {},
      isKritischeDestination: { required },
      automatischeAufgabeMtzErreicht: { required },
      automatischeAufgabeTktl: { required },
      automatischeAufgabeFahrzeugpruefung: { required },
    },
  },
  methods: {
    countryNameToCode(countryName) {
      const entry = Object.entries(alleLaender()).find(([, name]) => name === countryName);
      return entry ? entry[0] : null;
    },
    setFormToReise(reise) {
      this.form.reisekuerzel = reise.reisekuerzel;
      this.form.country = this.countryNameToCode(reise.country) || reise.country;
      this.form.weitereLaender = reise.weitereLaender || [];
      this.form.region = reise.region;
      this.form.prioritaet = this.prioritaetOptions[this.reise.prioritaet].value;
      this.form.titel = reise.titel;
      this.form.regionsmanager = reise.regionsmanager;
      this.form.einreisebestimmungenLink = reise.einreisebestimmungenLink;
      this.form.isKritischeDestination = reise.isKritischeDestination;
      this.form.automatischeAufgabeMtzErreicht = reise.automatischeAufgabeMtzErreicht;
      this.form.automatischeAufgabeTktl = reise.automatischeAufgabeTktl;
      this.form.automatischeAufgabeFahrzeugpruefung = reise.automatischeAufgabeFahrzeugpruefung;
    },
    validateState(name, mode = false) {
      if (!mode) {
        const { $dirty, $error } = this.$v.form[name];
        return $dirty ? !$error : null;
      }
      if (this.$v.form[name].required != null && !this.$v.form[name].required) {
        return this.$t('FORMS.REQUIREDFIELD');
      }

      if (this.$v.form[name].alpha != null && !this.$v.form[name].alpha) {
        return this.$t('FORMS.BUCHSTABEN');
      }

      if (this.$v.form[name].minLength != null && !this.$v.form[name].minLength) {
        return this.$t('FORMS.BETWEENCHARACTERS', { length: this.$v.form[name].$params.minLength.min });
      }

      if (this.$v.form[name].maxLength != null && !this.$v.form[name].maxLength) {
        return this.$t('FORMS.BETWEENCHARACTERS', { length: this.$v.form[name].$params.minLength.min });
      }

      if (this.$v.form[name].between != null && !this.$v.form[name].between) {
        return this.$t('FORMS.MAXIMUMCHARACTERS', {
          min: this.$v.form[name].$params.between.min,
          max: this.$v.form[name].$params.between.max,
        });
      }
    },
    async save() {
      const reise = {
        ...this.form,
        id: this.reise.id,
        prioritaet: Object.entries(this.prioritaetOptions).find(
          prio => prio[1].value == this.form.prioritaet
        )[0],
      };
      await this.$store
        .dispatch(UPDATE_REISE, {
          reise,
        })
        .then(resp => {
          if (resp.status === 200) {
            this.toast('Reise erfolgreich bearbeitet.');
          }
        });
    },
  },
};
</script>
